import React, { useMemo } from 'react';
import { type Chart, type ChartRenderCallbackFunction } from 'highcharts';
import { Card } from '../../../../../../../../CommonComponents/OPL/Card/Card';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Tooltip } from '../../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Icon } from '../../../../../../../../CommonComponents/OPL/Icon/Icon';
import { SplineChart } from '../../../../../../../../CommonComponents/OPL/charts/SplineChart/SplineChart';
import { MarginProps } from '../../../../../../../../theme/styledProps';
import { useTheme } from '../../../../../../../../theme/theme';
import { MARKET_CONDITIONS, ScenarioProjectionData } from '../../../../tabs/MonteCarlo/MonteCarlo.meta';
import { formatCurrency } from '../../../../../../../../CommonUtils/formatNumber';
import {
  StatsRow,
  StatItem,
  ColorBar,
  LegendRow,
  ClickableLegendItem,
  LegendSwatch,
} from './ScenarioProjection.styles';
import { chartRenderEvent } from '../../../RetirementChart/utils/chartRenderEvent';
import { MonteCarloPercentilesDTO } from '../../../../../../types/monteCarlo.types';

interface Props extends MarginProps {
  data: ScenarioProjectionData;
  hiddenSeries: Set<string>;
  onToggleSeries: (name: string) => void;
  milestoneData: MonteCarloPercentilesDTO | null;
}

type ChartRenderEvent = Parameters<ChartRenderCallbackFunction>[0];

export const ScenarioProjection = ({
  data,
  hiddenSeries,
  onToggleSeries,
  milestoneData,
  ...rest
}: Props) => {
  const theme = useTheme();
  const { series, categories, stats } = data;

  const visibleSeries = useMemo(() => series.filter(s => !hiddenSeries.has(s.name)), [
    series,
    hiddenSeries,
  ]);

 const chartConfig = {
    chart: {
      type: 'areaspline',
      height: 400,
      spacingRight: 40,
      spacingBottom: 96,
      events: {
        render(this: Chart, _evt: ChartRenderEvent) {
          if (milestoneData) {
            chartRenderEvent(this, milestoneData, theme, 'montecarlo');
          }
        },
      },
    },
    legend: { enabled: false },
    tooltip: {
      shared: true,
      useHTML: true,
      headerFormat: '<div style="font-weight:600;margin-bottom:4px">{point.key}</div>',
      pointFormat:
        '<div style="display:flex;align-items:center;gap:6px;margin:2px 0">' +
        '<span style="color:{series.color};font-size:16px">\u25CF</span>' +
        '<span>{series.name}:</span>' +
        // eslint-disable-next-line no-template-curly-in-string
        '<span style="font-weight:600">${point.y:,.0f}</span>' +
        '</div>',
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.08,
        lineWidth: 2.5,
        marker: { enabled: false },
      },
    },
  };

  return (
    <Box {...rest}>
      <Card p="24" hasBorder>
        <Text variant="cardTitle" mb="16">
          Projected Portfolio Balance
        </Text>

        <SplineChart series={visibleSeries} categories={categories} customSettings={chartConfig} />

        <LegendRow>
          {series.map(s => {
            const isHidden = hiddenSeries.has(s.name);
            return (
              <ClickableLegendItem
                key={s.name}
                isHidden={isHidden}
                onClick={() => onToggleSeries(s.name)}
              >
                <LegendSwatch bg={isHidden ? '#D1D5DB' : s.color} />
                <Text
                  fontSize="small"
                  fontWeight={500}
                  color={isHidden ? 'neutral.400' : 'neutral.600'}
                >
                  {s.name}
                </Text>
              </ClickableLegendItem>
            );
          })}
        </LegendRow>

        <Box mt="24">
          <Text fontSize="small" fontWeight={600} color="neutral.500" mb="8">
            Projected Value at End of Plan
          </Text>
          <StatsRow>
            {stats.map(stat => {
              const isHidden = hiddenSeries.has(stat.label);
              const trialsPct = MARKET_CONDITIONS.find(({ category }) => category === stat.label);
              return (
                <StatItem
                  key={stat.label}
                  isFocused={stat.isFocused && !isHidden}
                  isHidden={isHidden}
                  onClick={() => onToggleSeries(stat.label)}
                >
                  <ColorBar bg={isHidden ? theme.colors.neutral['300'] : stat.color} />
                  <Box>
                    <Box display="flex" alignItems="center" mb="4">
                      <Text
                        fontSize="small"
                        fontWeight={500}
                        color={isHidden ? 'neutral.400' : 'neutral.500'}
                        mr="4"
                      >
                        {stat.label}
                      </Text>
                      {trialsPct != null && (
                        <Tooltip
                          content={`${100 - trialsPct.percentile}% of the trials performed as good as or better than the results displayed here.`}
                          placement="top"
                        >
                          <Icon
                            className="far fa-info-circle"
                            color={isHidden ? 'neutral.300' : 'neutral.400'}
                            fontSize="small"
                          />
                        </Tooltip>
                      )}
                    </Box>
                    <Text
                      fontSize="h4"
                      fontWeight={stat.isFocused && !isHidden ? 600 : 500}
                      color={isHidden ? 'neutral.400' : 'neutral.800'}
                      variant="tabular"
                      textAlign="left"
                    >
                      {formatCurrency(stat.value)}
                    </Text>
                  </Box>
                </StatItem>
              );
            })}
          </StatsRow>
        </Box>
      </Card>
    </Box>
  );
};
